// Welke invulsessie hoort bij dit bezoek aan een openbare vragenlijst?
//
// Eén invulling = één bezoek aan de link (inbox #171). Tot 2026-10-02 las de
// pagina `sessionStorage.sessionId` en wiste die nooit: twee invullers achter
// elkaar in hetzelfde tabblad (de tablet van een coach, de telefoon van een
// fixer) deelden één formulier en telden als één respondent — en met de
// adresstap kwamen de antwoorden van het tweede huishouden bij het eerste.
//
// Regel:
// - Een nieuwe navigatie (QR-scan, link openen) → altijd een nieuwe sessie.
// - Herladen of terug/vooruit → de lopende sessie van DEZE link, zolang de
//   vragenlijst niet af is. Dat is waarvoor de opslag er is: per ongeluk
//   verversen mag je antwoorden niet in een tweede formulier splitsen.
// - Afgerond → de volgende keer altijd nieuw (ook 'Opnieuw', dat herlaadt).
//
// `sessionStorage.sessionId` blijft de HUIDIGE sessie aanwijzen: de
// AI-interviewer leest hem daar.
// AI-CONTEXT: See docs/features/adres-koppeling-algemene-qr.md

const CURRENT_KEY = 'sessionId'
const LINK_PREFIX = 'questionnaireSession:'

/**
 * Sleutel van één link: zelfde vragenlijst, meetmoment, onderzoek en respondent.
 * @param {{questionnaireId: string, moment: string, research: string, persona: string}} link
 * @returns {string}
 */
const sessionLinkKey = ({ questionnaireId, moment, research, persona }) =>
    `${LINK_PREFIX}${questionnaireId || ''}/${moment || ''}/${research || ''}/${persona || ''}`

const RESUME_TYPES = ['reload', 'back_forward']

// Opslag kan geblokkeerd zijn (privémodus, strenge browserinstelling). Dan is
// er niets te hervatten: een nieuwe sessie is het juiste antwoord, geen fout.
const read = (storage, key) => {
    try { return storage.getItem(key) } catch { return null }
}
const write = (storage, key, value) => {
    try { storage.setItem(key, value) } catch { /* zie read */ }
}
const remove = (storage, key) => {
    try { storage.removeItem(key) } catch { /* zie read */ }
}

/**
 * @param {object} input
 * @param {Storage} input.storage - sessionStorage
 * @param {string} input.linkKey - sessionLinkKey(...)
 * @param {string} [input.navigationType] - PerformanceNavigationTiming.type
 * @param {() => string} input.newId - uuid-generator
 * @returns {string} De sessie voor dit bezoek
 */
const resolveQuestionnaireSession = ({ storage, linkKey, navigationType, newId }) => {
    const stored = RESUME_TYPES.includes(navigationType) ? read(storage, linkKey) : null
    const sessionId = stored || newId()
    write(storage, linkKey, sessionId)
    write(storage, CURRENT_KEY, sessionId)
    return sessionId
}

/**
 * De vragenlijst is af: de volgende keer begint deze link een nieuwe sessie.
 * @param {{storage: Storage, linkKey: string}} input
 */
const markQuestionnaireSessionDone = ({ storage, linkKey }) => remove(storage, linkKey)

/** Het navigatietype van deze pagina ('navigate', 'reload', 'back_forward'). */
const currentNavigationType = () => {
    try {
        return window.performance?.getEntriesByType?.('navigation')?.[0]?.type
    } catch {
        return undefined
    }
}

export { resolveQuestionnaireSession, markQuestionnaireSessionDone, sessionLinkKey, currentNavigationType }
